import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { map, take, tap } from 'rxjs/operators';
import { PROVIDER_NAME_BY_ID } from '#common/constants/providers';
import { PATH_NEW_SESSION } from '#common/constants/top';
import { RepoStatusEnum } from '#common/enums/repo-status.enum';
import { ResponseInfoStatusEnum } from '#common/enums/response-info-status.enum';
import { SessionStatusEnum } from '#common/enums/session-status.enum';
import { SessionTypeEnum } from '#common/enums/session-type.enum';
import { ToBackendRequestInfoNameEnum } from '#common/enums/to/to-backend-request-info-name.enum';
import type { SessionApi } from '#common/zod/backend/session-api';
import type { SessionApiX } from '#common/zod/front/session-api-x';
import type {
  ToBackendArchiveSessionRequestPayload,
  ToBackendArchiveSessionResponse
} from '#common/zod/to-backend/sessions/to-backend-archive-session';
import type {
  ToBackendGetSessionsListRequestPayload,
  ToBackendGetSessionsListResponse
} from '#common/zod/to-backend/sessions/to-backend-get-sessions-list';
import type {
  ToBackendPauseEditorSessionRequestPayload,
  ToBackendPauseEditorSessionResponse
} from '#common/zod/to-backend/sessions/to-backend-pause-editor-session';
import { makeTitle } from '#front/app/functions/make-title';
import { NavQuery } from '#front/app/queries/nav.query';
import { RepoQuery } from '#front/app/queries/repo.query';
import { SessionQuery } from '#front/app/queries/session.query';
import { SessionBundleQuery } from '#front/app/queries/session-bundle.query';
import { SessionEventsQuery } from '#front/app/queries/session-events.query';
import { SessionsQuery } from '#front/app/queries/sessions.query';
import { UiQuery } from '#front/app/queries/ui.query';
import { ApiService } from '#front/app/services/api.service';
import { MyDialogService } from '#front/app/services/my-dialog.service';
import { NavigateService } from '#front/app/services/navigate.service';

let SESSIONS_SPINNER_NAME = 'sessionsRefresh';

@Component({
  standalone: false,
  selector: 'm-sessions',
  templateUrl: './sessions.component.html'
})
export class SessionsComponent implements OnInit {
  sessions: SessionApiX[] = [];
  sessionsLoaded = false;
  hasMoreArchived = false;
  isLoadingArchived = false;
  archivedLastCreatedTs: number = undefined;
  currentSession: SessionApi;
  isRefreshing = false;
  sessionStatusArchived = SessionStatusEnum.Archived;
  sessionTypeEnum = SessionTypeEnum;
  spinnerName = SESSIONS_SPINNER_NAME;
  sessions$ = this.sessionsQuery.sessions$.pipe(
    tap(x => {
      this.sessions = x
        .filter(s => s.type === SessionTypeEnum.Editor)
        .map(s =>
          Object.assign({}, s, <SessionApiX>{
            displayTitle: makeTitle(s),
            providerLabel: PROVIDER_NAME_BY_ID[s.providerId] || s.providerId
          })
        )
        .sort((a, b) => {
          let aArchived = a.status === SessionStatusEnum.Archived ? 1 : 0;
          let bArchived = b.status === SessionStatusEnum.Archived ? 1 : 0;
          return aArchived - bArchived || b.createdTs - a.createdTs;
        });
      this.cd.detectChanges();
    })
  );

  hasMoreArchived$ = this.sessionsQuery.hasMoreArchived$.pipe(
    tap(x => {
      this.hasMoreArchived = x;
      this.cd.detectChanges();
    })
  );

  session$ = this.sessionQuery.select().pipe(
    tap(x => {
      this.currentSession = x;
      this.cd.detectChanges();
    })
  );

  selectedRepoId: string;
  nav$ = this.navQuery.select().pipe(
    tap(x => {
      this.selectedRepoId = x.repoId;
      this.cd.detectChanges();
    })
  );

  constructor(
    private sessionsQuery: SessionsQuery,
    private sessionQuery: SessionQuery,
    private sessionEventsQuery: SessionEventsQuery,
    private uiQuery: UiQuery,
    private navQuery: NavQuery,
    private apiService: ApiService,
    private navigateService: NavigateService,
    private router: Router,
    private cd: ChangeDetectorRef,
    private spinner: NgxSpinnerService,
    private myDialogService: MyDialogService,
    private sessionBundleQuery: SessionBundleQuery,
    private repoQuery: RepoQuery
  ) {}

  ngOnInit() {
    this.loadSessions();
  }

  loadSessions() {
    let projectId: string;

    this.navQuery.projectId$.pipe(take(1)).subscribe(x => {
      projectId = x;
    });

    let currentSessionId =
      this.currentSession?.sessionId ?? this.sessionQuery.getValue()?.sessionId;

    let payload: ToBackendGetSessionsListRequestPayload = {
      projectId: projectId,
      currentSessionId: currentSessionId,
      sessionType: SessionTypeEnum.Editor
    };

    this.isRefreshing = true;
    this.spinner.show(SESSIONS_SPINNER_NAME);
    this.cd.detectChanges();

    this.apiService
      .req({
        pathInfoName: ToBackendRequestInfoNameEnum.ToBackendGetSessionsList,
        payload: payload
      })
      .pipe(
        map((resp: ToBackendGetSessionsListResponse) => {
          if (resp.info?.status === ResponseInfoStatusEnum.Ok) {
            let sessions = resp.payload.sessions;

            if (currentSessionId) {
              let freshCurrentSession = sessions.find(
                s => s.sessionId === currentSessionId
              );
              if (freshCurrentSession) {
                this.sessionQuery.update(freshCurrentSession);
              }
            }

            let hasMoreArchived = resp.payload.hasMoreArchived ?? false;

            this.sessionsQuery.updatePart({
              sessions: sessions,
              isListLoaded: true,
              hasMoreArchived: hasMoreArchived
            });
          }

          this.isRefreshing = false;
          this.spinner.hide(SESSIONS_SPINNER_NAME);
          this.sessionsLoaded = true;
          this.archivedLastCreatedTs = undefined;
          this.cd.detectChanges();
        }),
        take(1)
      )
      .subscribe();
  }

  newSession() {
    let ar = this.router.url.split('?')[0].split('/');

    let isNewSessionRoute = ar[ar.length - 1] === PATH_NEW_SESSION;

    if (!this.currentSession?.sessionId && isNewSessionRoute) {
      return;
    }

    this.uiQuery.updatePart({ showContent: false });
    this.sessionBundleQuery.reset();
    this.sessionEventsQuery.reset();

    let session = this.sessionQuery.getValue();
    this.sessionQuery.update({ ...session, firstMessage: undefined });
    this.navigateService.navigateToBuilder({
      // repoId: PROD_REPO_ID,
      // branchId: this.navQuery.getValue().projectDefaultBranch,
      newSession: true
    });
  }

  pauseSession(event: MouseEvent, session: SessionApiX) {
    event.stopPropagation();

    let sessionId = session.sessionId;

    let payload: ToBackendPauseEditorSessionRequestPayload = {
      sessionId: sessionId
    };

    this.apiService
      .req({
        pathInfoName: ToBackendRequestInfoNameEnum.ToBackendPauseEditorSession,
        payload: payload,
        showSpinner: true
      })
      .pipe(
        map((resp: ToBackendPauseEditorSessionResponse) => {
          let respSession = resp.payload.session;

          if (respSession.status === 'Archived') {
            this.updatedArchivedSessions({
              session: session,
              respSession: respSession
            });
          } else {
            let sessions = this.sessionsQuery.getValue().sessions;
            let updated = sessions.map(s =>
              s.sessionId === sessionId
                ? {
                    ...s,
                    status: respSession.status,
                    archiveReason: respSession.archiveReason,
                    pauseReason: respSession.pauseReason
                  }
                : s
            );
            this.sessionsQuery.updatePart({ sessions: updated });

            let currentSession = this.sessionQuery.getValue();

            if (currentSession?.sessionId === sessionId) {
              this.sessionQuery.update({
                ...currentSession,
                status: respSession.status,
                archiveReason: respSession.archiveReason,
                pauseReason: respSession.pauseReason
              });
            }
          }
        }),
        take(1)
      )
      .subscribe();
  }

  archiveSession(event: MouseEvent, session: SessionApiX) {
    event.stopPropagation();

    let sessionId = session.sessionId;

    let payload: ToBackendArchiveSessionRequestPayload = {
      sessionId: sessionId
    };

    this.apiService
      .req({
        pathInfoName: ToBackendRequestInfoNameEnum.ToBackendArchiveSession,
        payload: payload,
        showSpinner: true
      })
      .pipe(
        map((resp: ToBackendArchiveSessionResponse) => {
          let respSession = resp.payload.session;

          this.updatedArchivedSessions({
            session: session,
            respSession: respSession
          });
        }),
        take(1)
      )
      .subscribe();
  }

  private updatedArchivedSessions(item: {
    session: SessionApiX;
    respSession: SessionApi;
  }) {
    let { session, respSession } = item;
    let sessionId = session.sessionId;

    let sessions = this.sessionsQuery.getValue().sessions;

    if (
      this.archivedLastCreatedTs !== undefined &&
      session.createdTs >= this.archivedLastCreatedTs
    ) {
      // Move to archived section
      let others = sessions.filter(s => s.sessionId !== sessionId);
      let nonArchived = others.filter(s => s.status !== 'Archived');
      let archived = others.filter(s => s.status === 'Archived');
      let archivedSession = {
        ...session,
        status: respSession.status,
        archiveReason: respSession.archiveReason,
        pauseReason: respSession.pauseReason
      };

      let insertIdx = archived.findIndex(
        s => s.createdTs < archivedSession.createdTs
      );
      if (insertIdx === -1) {
        archived.push(archivedSession);
      } else {
        archived.splice(insertIdx, 0, archivedSession);
      }

      this.sessionsQuery.updatePart({
        sessions: [...nonArchived, ...archived],
        hasMoreArchived: true
      });
    } else {
      // Remove from list
      let updated = sessions.filter(s => s.sessionId !== sessionId);
      this.sessionsQuery.updatePart({
        sessions: updated,
        hasMoreArchived: true
      });
    }

    this.cd.detectChanges();

    let currentSession = this.sessionQuery.getValue();
    if (currentSession?.sessionId === sessionId) {
      this.navigateService.navigateToBuilder();
    }
  }

  loadArchivedSessions() {
    let projectId: string;

    this.navQuery.projectId$.pipe(take(1)).subscribe(x => {
      projectId = x;
    });

    let payload: ToBackendGetSessionsListRequestPayload = {
      projectId: projectId,
      includeArchived: true,
      archivedLimit: 10,
      archivedLastCreatedTs: this.archivedLastCreatedTs,
      currentSessionId: this.currentSession?.sessionId,
      sessionType: SessionTypeEnum.Editor
    };

    this.isLoadingArchived = true;
    this.cd.detectChanges();

    this.apiService
      .req({
        pathInfoName: ToBackendRequestInfoNameEnum.ToBackendGetSessionsList,
        payload: payload
      })
      .pipe(
        map((resp: ToBackendGetSessionsListResponse) => {
          if (resp.info?.status === ResponseInfoStatusEnum.Ok) {
            let sessions = resp.payload.sessions;

            if (this.currentSession?.sessionId) {
              let freshCurrentSession = sessions.find(
                s => s.sessionId === this.currentSession?.sessionId
              );
              if (freshCurrentSession) {
                this.sessionQuery.update(freshCurrentSession);
              }
            }

            let hasMoreArchived = resp.payload.hasMoreArchived ?? false;

            this.sessionsQuery.updatePart({
              sessions: sessions,
              hasMoreArchived: hasMoreArchived
            });
            let archivedSessions = resp.payload.sessions.filter(
              s =>
                s.status === SessionStatusEnum.Archived &&
                s.sessionId !== this.currentSession?.sessionId
            );
            if (archivedSessions.length > 0) {
              this.archivedLastCreatedTs =
                archivedSessions[archivedSessions.length - 1].createdTs;
            }
          }

          this.isLoadingArchived = false;
          this.cd.detectChanges();
        }),
        take(1)
      )
      .subscribe();
  }

  renameSession(event: MouseEvent, session: SessionApiX) {
    event.stopPropagation();
    this.myDialogService.showEditSessionTitle({
      apiService: this.apiService,
      sessionId: session.sessionId,
      title: makeTitle(session)
    });
  }

  deleteSession(event: MouseEvent, session: SessionApiX) {
    event.stopPropagation();
    this.myDialogService.showDeleteSession({
      apiService: this.apiService,
      sessionId: session.sessionId,
      title: makeTitle(session)
    });
  }

  trackBySessionId(_index: number, session: SessionApiX) {
    return session.sessionId;
  }

  openSession(session: SessionApi) {
    if (session.sessionId === this.currentSession?.sessionId) {
      return;
    }

    this.uiQuery.updatePart({ showContent: false });
    this.sessionBundleQuery.reset();
    this.sessionEventsQuery.reset();
    this.sessionQuery.update({ ...session, firstMessage: undefined });

    let currentRepoId = this.navQuery.getValue().repoId;

    if (session.repoId !== currentRepoId) {
      this.repoQuery.updatePart({
        repoStatus: RepoStatusEnum.Ok,
        changesToCommit: [],
        changesToPush: []
      });
    }

    this.navigateService.navigateToSession({
      sessionId: session.sessionId,
      repoId: session.repoId,
      branchId: session.branchId,
      envId: session.envId
    });
  }
}
